const _toString = Object.prototype.toString;
export const toString = (obj: any): string => _toString.call(obj);

export class TYPES {
  static readonly UNDEFINED = toString(void 0);
  static readonly NULL = toString(null);
  static readonly STRING = toString('');
  static readonly NUMBER = toString(0);
  static readonly BOOLEAN = toString(false);
  static readonly FUNCTION = toString(Function.prototype);

  // symbol 可能会报错，直接写死吧
  static readonly SYMBOL = '[object Symbol]';
  static readonly OBJECT = toString({});
  static readonly ARRAY = toString([]);

  static readonly DATE = toString(new Date());
  static readonly ERROR = toString(new Error());
};

/**
 * 延迟一段时间(秒)
 */
export async function sleep(timeout: number = 0): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, timeout * 1000);
  });
}

/**
 * 遍历对象所有属性
 * 移除左右两边空格
 */
export function trim<T>(data: T): T {
  if (typeof data == typeof {}) {
    for (let prop in data) {
      data[prop] = trim(data[prop]);
    }
  } else if (typeof data == typeof '') {
    // @ts-ignore
    data = data.trim();
  }
  return data;
}

/**
 * 不足位补0
 */
export function addZero(num: string | number, length: number = 2): string {
  return String(num).padStart(length, '0');
}

/**
 * 处理url请求参数
 * 需要手动加?
 */
export function objectToUrlParams(params: object): string {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');
}

/**
 * 请求参数转对象
 */
export function urlParamsToObject(urlParams: string): object {
  return urlParams
    .substr(1)
    .split('&')
    .map(entry => entry.split('='))
    .reduce((params:object, [key, value]) => {
      // @ts-ignore
      params[decodeURIComponent(key)] = decodeURIComponent(value);
      return params;
    }, {});
}

/**
 * 判断是不是一个没有任何属性的对象
 */
export function isPlainObject(obj: any): boolean {
  if (typeof obj != 'object') {
    return false;
  }
  for (const _ in obj) {
    return false;
  }
  return true;
}

/**
 * 防抖
 */
export function debounce(callback: Function, timeout: number): Function {
  let timer: any;

  return function (...args: any[]) {
    timer && clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
      timer = null;
    }, timeout);
  };
}

/**
 * 判断是否是promise
 */
export function isPromise(val: any): boolean {
  return val && TYPES.FUNCTION == toString(val.then) && TYPES.FUNCTION == toString(val.catch);
}

export function identify(val: any): any {
  return val;
}

/**
 * 重试函数
 */
export function retry(callback: Function, times: number = 2): Function {
  return function (...args: any[]): any {
    let cnt = 0;

    function catchError(err: any, ...args: any[]) {
      if (cnt++ < times) {
        return recursion(...args);
      }
      throw new Error(err.message);
    }

    function recursion(...args: any[]): any {
      try {
        let data = callback(...args);
        if (isPromise(data)) {
          return data.then(identify).catch((err: Error) => catchError(err, ...args));
        } else {
          return data;
        }
      } catch (err) {
        return catchError(err, ...args);
      }
    }

    return recursion(...args);
  }
}

type Options = {
  leading: boolean,
  timeout: number,
};

const DEFAULT_THROTTLE_OPTIONS = {
  timeout: 500,
  leading: false,
};

/**
 * 节流
 */
export function throttle(callback: Function, options: Options = DEFAULT_THROTTLE_OPTIONS) {
  return options.leading ? leading(callback, options.timeout) : trailing(callback, options.timeout);
}

/**
 * 立即执行节流
 */
function leading(callback: Function, timeout: number) {
  let timer: any = null;
  let hadCall: boolean = false;
  let args: any[];

  function run(context: any): void {
    if (!timer) {
      timer = setTimeout(() => {
        if (hadCall) {
          hadCall = false;
          timer = null;
          run(context);
        } else {
          timer && clearTimeout(timer);
          timer = null;
        }
      }, timeout);

      callback.apply(context, args);
    } else {
      hadCall = true;
    }
  }

  return function (...params: any[]): any {
    args = params;
    // @ts-ignore
    run(this);
  };
}

/**
 * 延迟节流
 */
export function trailing(callback: Function, timeout: number) {
  let timer: any = null;
  let args: any[];
  return function (...params: any[]):any {
    args = params;
    if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        // @ts-ignore
        callback.apply(this, args);
      }, timeout);
    }
  };
}

/**
 * 是否是数字
 */
export function isInteger(number: any) {
  const type = toString(number);
  if (TYPES.NULL == type || TYPES.UNDEFINED == type) {
    return false;
  }
  return /^[0-9]+$/.test(number.toString());
}

/**
 * 隐藏部分手机号
 */
export function hiddenMobile(mobile: string): string {
  mobile = mobile || '';
  return mobile.replace(/\d{1,4}(?=(\d{4}$))/, ($0) => {
    return ''.padStart($0.length, '*');
  });
}

/**
 * 反转数组中的某一段(不包括end)
 */
export function reverseRange(arr: any[], start: number, end: number): void {
  let middle = ((end - start) >> 1) + start;
  for (let i = start; i < middle; i++) {
    swap(arr, i, end - i - 1 + start);
  }
}

/**
 * 交换数组中的两个元素
 * 不保证下标是否规范
 */
function swap(arr: any[], index1: number, index2: number): void {
  let value = arr[index1];
  arr[index1] = arr[index2];
  arr[index2] = value;
}

/**
 * 重入函数,不绑定 this
 * 和实际的重入函数可能不太一样
 * 我要做的是:
 * 1. 多次调用函数，
 * 2. 后一次调用时，前一次的调用未结束
 * 3. 丢弃前一次的调用结果
 *
 * 对js来说，同步的函数是不太可能会被中断的
 * 所以我做的其实只能中断异步函数
 */
export const reentrant = (callback: (...args: any[]) => Promise<any>) => {
  let id = 0;
  return async (...args: any[]) => {
    id++;
    const currentId = id;
    const result = await callback.apply(null, args);
    if (id == currentId) {
      return result;
    }
    return new Promise(() => {});
  };
};

/**
 * 二分搜索
 * 找到第一个满足条件的下标
 * @param values - 有序数组
 * @param target - 目标值
 * @param compare - 比较函数: 返回 <0(a < b), 0(a == b), >0(a > b)
 */
export const findFirstIndex = <T>(values: T[], target: T, compare?: (a: T, b: T) => number) => {
  if (!compare) {
    compare = (a, b) => {
      // @ts-ignore
      return a - b;
    };
  }

  // 左闭右开
  let left = 0;
  let right = values.length;

  while (left < right) {
    let middle = ((right - left) >> 1) + left;
    let cmp = compare(values[middle], target);

    // 中点小于目标值
    if (0 > cmp) {
      left = middle + 1;
    // 找到满足要求的下标
    } else if (0 <= cmp) {
      if (
        // 只有一个数可选
        left == middle ||
        // 左边没有大于等于目标值的下标
        (0 < middle && 0 > compare(values[middle - 1], target))
      ) {
        return middle;
      }
      right = middle;
    }
  }

  return -1;
};
